import { HTMLAttributes, forwardRef } from 'react'
import { motion } from 'framer-motion'

interface SkeletonProps extends HTMLAttributes<HTMLDivElement> {
  animation?: 'pulse' | 'shimmer' | 'none'
}

export const Skeleton = forwardRef<HTMLDivElement, SkeletonProps>(
  ({ className = '', animation = 'pulse', ...props }, ref) => {
    const animations = {
      pulse: 'animate-pulse',
      shimmer: 'animate-shimmer bg-gradient-to-r from-dark-bg-tertiary via-dark-bg-card to-dark-bg-tertiary',
      none: '',
    }
    
    return (
      <motion.div
        ref={ref}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className={`
          bg-dark-bg-tertiary rounded-md
          ${animations[animation]}
          ${className}
        `}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        {...props as any}
      />
    )
  }
)

Skeleton.displayName = 'Skeleton'

// Preset skeleton components
export const SkeletonText = ({ lines = 3 }: { lines?: number }) => {
  return (
    <div className="space-y-2 w-full">
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton
          key={i}
          className={`h-4 ${i === lines - 1 ? 'w-3/4' : 'w-full'}`}
        />
      ))}
    </div>
  )
}

export const SkeletonCard = () => {
  return (
    <div className="p-5 space-y-4">
      <div className="flex items-center gap-3">
        <Skeleton className="w-10 h-10 rounded-full" />
        <div className="flex-1 space-y-2">
          <Skeleton className="h-4 w-1/3" />
          <Skeleton className="h-3 w-1/4" />
        </div>
      </div>
      <SkeletonText lines={2} />
    </div>
  )
}

export const SkeletonStats = () => {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="p-4 space-y-3">
          <Skeleton className="h-8 w-16" />
          <Skeleton className="h-4 w-20" />
        </div>
      ))}
    </div>
  )
}
